// src/shared/components/Sidebar/SidebarMenu.tsx
import type { KeyboardEvent } from 'react';

import { memo, useEffect, useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { getEnabledCategories } from '../../../config/menu.config';
import { getLogger } from '../../../lib/logger/logger';
import { useRegion } from '../../hooks/useRegion';
import { useSidebarMenu } from '../../hooks/useSidebarMenu';

const logger = getLogger('SidebarMenu');

interface SidebarMenuProps {
  isOpen?: boolean;
  onToggle?: () => void;
}

// ✅ P1-3: Memoized component to prevent unnecessary re-renders
export const SidebarMenu = memo(function SidebarMenu({ isOpen = true, onToggle }: SidebarMenuProps) {
  const {
    expandedCategories,
    featureListHeight,
    selectedFeature,
    isLoading,
    error,
    sidebarRef,
    handleCategoryClick,
    handleFeatureSelect,
    categories,
    isEmptySDK,
  } = useSidebarMenu({ isOpen });

  const { t } = useTranslation();
  const { isOverseas } = useRegion();
  // Debug: Log when categories change
  useEffect(() => {
    logger.info('[SidebarMenu] Render with categories:', {
      categoryCount: categories.length,
      categoryIds: categories.map(c => c.id),
      isOverseas,
    });
  }, [categories, isOverseas]);

  // Keyboard handler for accessibility (Enter/Space triggers click)
  const handleKeyDown = useCallback(
    (callback: () => void) => (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        callback();
      }
    },
    [],
  );

  // 해외/국내 + 메뉴 설정을 고려해 안전하게 카테고리 라벨을 계산
  const getCategoryLabel = (categoryId: string): string => {
    // 해외향에서 HCell 표기 오버라이드
    if (isOverseas && categoryId === 'hcell') {
      return t('sidebar.categories.hcellOverseas');
    }

    // menu.config 에서 카테고리 탐색
    const enabledCategories = getEnabledCategories();
    const cfg = enabledCategories.find(
      (c: unknown): c is { id: string; titleKey?: string } =>
        typeof c === 'object' && c !== null && (c as { id?: unknown }).id === categoryId,
    );

    const key = cfg?.titleKey;
    return key ? t(key) : categoryId;
  };

  // 닫힌 상태일 때는 사이드바 미노출
  if (!isOpen) {
    return null;
  }

  // 로딩 상태
  if (isLoading) {
    return (
      <div className='w-full bg-white border-r border-gray-200 h-full flex items-center justify-center'>
        <div className='text-gray-500'>{t('loading')}</div>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div className='w-full bg-white border-r border-gray-200 h-full flex items-center justify-center'>
        <div className='text-red-500 text-center p-4'>
          <p>{t('error')}</p>
          <p className='text-sm mt-2'>{typeof error === 'string' ? error : String(error)}</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={sidebarRef} className='w-full bg-white border-r border-gray-200 h-full flex flex-col'>
      {/* 메인 컨텐츠 영역 */}
      <div className='flex-1 flex flex-col'>
        <div className='p-4' style={{ paddingTop: '20px' }}>
          {/* 헤더와 토글 버튼 */}
          <div className='flex items-center justify-between mb-6'>
            <h2 className='text-2xl font-bold text-black'>{t('sidebar.title')}</h2>
            <button
              onClick={onToggle}
              className='p-1 rounded-md hover:bg-gray-100 transition-colors'
              title={t('sidebar.closeSidebar')}
            >
              <svg className='w-4 h-4 text-gray-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
              </svg>
            </button>
          </div>

          {/* 모든 SDK 표시 */}
          {categories.map(category => {
            const isExpanded = expandedCategories[category.id];
            const isEmpty = isEmptySDK(category.id);

            return (
              <div key={category.id} className='mb-2'>
                {/* 카테고리 헤더 (토글/선택) */}
                <button
                  type='button'
                  className={`w-full flex items-center px-3 py-2 cursor-pointer rounded transition-colors duration-150 text-left ${
                    // 세부 기능이 없는 SDK가 선택된 경우 파란색 배경
                    isEmpty &&
                    selectedFeature?.category === category.id &&
                    selectedFeature?.id === category.id
                      ? 'bg-blue-600 text-white font-medium shadow-sm'
                      : 'text-black hover:bg-blue-50'
                  }`}
                  onClick={() => handleCategoryClick(category.id)}
                  onKeyDown={handleKeyDown(() => handleCategoryClick(category.id))}
                >
                  {/* 토글 아이콘 자리: 모든 SDK에 동일 공간 할당 */}
                  <div className='w-4 h-4 mr-2 flex items-center justify-center'>
                    {!isEmpty && (
                      <svg
                        className={`w-4 h-4 transition-transform duration-200 ${
                          isExpanded ? 'transform rotate-90' : ''
                        } text-blue-600`}
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                      </svg>
                    )}
                  </div>

                  {/* ✅ 안전한 라벨 표시 */}
                  <span className='font-bold text-base'>{getCategoryLabel(category.id)}</span>
                </button>

                {/* 기능 목록 - 기능이 있는 SDK만 */}
                {isExpanded && !isEmpty && (
                  <div className='ml-[20px] mt-2 border-l-2 border-gray-200 pl-3 pr-0'>
                    <div
                      className='overflow-y-auto space-y-1'
                      style={{ maxHeight: `${featureListHeight}px` }}
                    >
                      {category.features.map(feature => {
                        if (!feature) return null;
                        const isSelected = selectedFeature?.id === feature.id;

                        return (
                          <button
                            key={feature.id}
                            type='button'
                            className={`w-full px-3 py-2 text-base cursor-pointer rounded transition-colors duration-150 truncate text-left ${
                              isSelected
                                ? 'bg-blue-600 text-white font-medium shadow-sm'
                                : 'text-black hover:bg-blue-50 hover:text-blue-700'
                            }`}
                            onClick={() => handleFeatureSelect(feature)}
                            onKeyDown={handleKeyDown(() => handleFeatureSelect(feature))}
                            title={feature.title}
                          >
                            {feature.title}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});
